import { Spacer } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FormLabel, HorizontalFormGroup, InputContainer } from '@frontend/components/elements/forms';
import type { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import type { ProfessionalSummaryDataSectionLoadResult } from '../../../../../../utils/professionalSummaryDataSectionLoadResult';
import type { ValidationErrors } from '../../../../../../utils/validation';
import type { IMedication } from '../../../../../interfaces/medication';
import type { Medications } from './medications';
import { MedicationsSelectActions } from '../../../../../../../common/enums/medicationsSelectActions';
import { YesOrNo } from '../../../../../../../common/enums/yesOrNo';
import { never } from '../../../../../../../common/utils/never';
import { translateKey } from '../../../../../../translate';
import { useEpisodeContext } from '../../../../../../utils/hooks/useEpisodeContext';
import { InputRadioGroup, LinkOnClick } from '../../../../../elements/common';
import { FormSection } from '../../../../../elements/forms/formSection/FormSection';
import { ProfessionalSummaryDataAction } from '../../../../professionalSummaryData/ProfessionalSummaryDataSection';
import { checkMedicationsHaveErrors } from '../../../validateMedications';
import {
    filterAndPrepareMedicationsForSaving,
    isCurrentMedication,
    loadProcedureMedications,
    loadRequestMedications,
    MedicationRecord,
} from './medications';
import { MedicationsAddEditTable } from './MedicationsAddEditTable';
import { MedicationsAddEditTableRow } from './MedicationsAddEditTableRow';
import { MedicationsCollapsible } from './MedicationsCollapsible';
import { MedicationsPickableTable } from './MedicationsPickableTable';
import { MedicationsPickableTableRow } from './MedicationsPickableTableRow';

interface IMedicationsAddEditProps {
    entity: ProcessEntity.request | ProcessEntity.procedure;
    entityId?: string;
    errors?: ValidationErrors;
    handleChangePatientUsesMedication: (patientUsesMedication: boolean) => Promise<void>;
    onChange: (medications: IMedication[]) => Promise<void>;
    patientUsesMedication: boolean | null;
    validateMedications: () => Promise<void>;
    validateOneCustomControl?: (name: string, index?: number) => Promise<void>;
    validatePatientUsesMedication: () => Promise<void>;
}

export function MedicationsAddEdit(props: IMedicationsAddEditProps) {
    const { patient } = useEpisodeContext();
    const [isHisMedicationsOpen, setIsHisMedicationsOpen] = useState(false);
    const [isManualMedicationsOpen, setIsManualMedicationsOpen] = useState(false);
    const [medications, setMedications] = useState<Medications>();

    const [hasPickableMedications, setHasPickableMedications] = useState(false);
    const [hisMedicationsCount, setHisMedicationsCount] = useState(0);
    const [manualMedicationsCount, setManualMedicationsCount] = useState(0);
    const [hisTableRows, setHisTableRows] = useState<React.ReactNode[]>([]);
    const [manualTableRows, setManualTableRows] = useState<React.ReactNode[]>([]);
    const [loadResult, setLoadResult] = useState<ProfessionalSummaryDataSectionLoadResult>();

    useEffect(() => {
        loadMedications();
    }, []);

    useEffect(() => {
        if (loadResult) {
            setLoadResult(loadResult);
        }
    }, [loadResult]);

    useEffect(() => {
        setMedicationsRows();
        props.validateMedications();
        if (medications) {
            props.onChange(filterAndPrepareMedicationsForSaving(medications));
        }
    }, [medications, props.errors]);

    if (!medications) {
        return null;
    }

    return (
        <FormSection title={translateKey('medications.medication')}>
            <HorizontalFormGroup
                hasErrors={props.errors?.has('patientUsesMedication') || checkMedicationsHaveErrors(props.errors)}
            >
                <FormLabel
                    isRequired
                    tooltipText={translateKey('medications.patientUsesMedicationTooltip')}
                >
                    {translateKey('medications.patientUsesMedication')}
                </FormLabel>
                <InputContainer>
                    <InputRadioGroup
                        automation="patientUsesMedication"
                        error={props.errors?.get('patientUsesMedication')}
                        labels={[translateKey('common.yes'), translateKey('common.no')]}
                        name="patientUsesMedication"
                        onChange={async (value) => handlePatientUsesMedicationChange(value)}
                        onDoValidate={async () => props.validatePatientUsesMedication()}
                        options={[YesOrNo.yes, YesOrNo.no]}
                        value={getPatientUsesMedicationValue()}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            {props.patientUsesMedication && (
                <>
                    {hasPickableMedications && (
                        <>
                            <MedicationsCollapsible
                                className={checkMedicationsHaveErrors(props.errors) ? 'has-danger' : ''}
                                extraHeaderElements={
                                    <Spacer
                                        direction="horizontal"
                                        spacing="small"
                                    >
                                        <LinkOnClick
                                            className="mr-2"
                                            onClick={async () => await selectActions(MedicationsSelectActions.all)}
                                        >
                                            {translateKey('professionalSummary.all')}
                                        </LinkOnClick>
                                        <LinkOnClick
                                            className="mr-2"
                                            onClick={async () => await selectActions(MedicationsSelectActions.current)}
                                        >
                                            {translateKey('professionalSummary.current')}
                                        </LinkOnClick>
                                        <LinkOnClick
                                            className="mr-2"
                                            onClick={async () => await selectActions(MedicationsSelectActions.none)}
                                        >
                                            {translateKey('professionalSummary.none')}
                                        </LinkOnClick>
                                    </Spacer>
                                }
                                header={translateKey('medications.addMedicationsFromHis')}
                                isOpen={isHisMedicationsOpen && hasPickableMedications}
                                onToggle={() => setIsHisMedicationsOpen(!isHisMedicationsOpen)}
                                subHeader={
                                    hisMedicationsCount
                                        ? translateKey(
                                              'medications.numberOfMedicationsSelected',
                                              hisMedicationsCount.toString()
                                          )
                                        : ''
                                }
                            >
                                <MedicationsPickableTable tableRows={hisTableRows} />
                            </MedicationsCollapsible>
                        </>
                    )}
                    <MedicationsCollapsible
                        className={checkMedicationsHaveErrors(props.errors) ? 'has-danger' : ''}
                        header={translateKey('medications.addMedicationsManually')}
                        isOpen={isManualMedicationsOpen}
                        onToggle={() => {
                            if (!checkMedicationsHaveErrors(props.errors) || !isManualMedicationsOpen) {
                                handleToggleManualMedications();
                            }
                        }}
                        subHeader={
                            manualMedicationsCount
                                ? translateKey(
                                      'medications.numberOfMedicationsManuallyAdded',
                                      manualMedicationsCount.toString()
                                  )
                                : ''
                        }
                    >
                        <MedicationsAddEditTable
                            addEmptyMedicationRecord={async () => await addEmptyMedicationRecord()}
                            tableRows={manualTableRows}
                        />
                    </MedicationsCollapsible>
                </>
            )}
        </FormSection>
    );

    async function handleToggleManualMedications() {
        setIsManualMedicationsOpen(!isManualMedicationsOpen);
        if (
            medications &&
            !medications.some((medication) => medication?.action === ProfessionalSummaryDataAction.add)
        ) {
            await addEmptyMedicationRecord();
        }
    }

    function getPatientUsesMedicationValue(): YesOrNo | null {
        let patientUsesMedicationValue: YesOrNo | null = null;
        if (props.patientUsesMedication !== null) {
            patientUsesMedicationValue = props.patientUsesMedication ? YesOrNo.yes : YesOrNo.no;
        }

        return patientUsesMedicationValue;
    }

    async function loadMedications() {
        let initialMedications;

        if (props.entity === 'request') {
            initialMedications = await loadRequestMedications(patient, props.entityId);
            setMedications(initialMedications);
        } else if (props.entity === 'procedure') {
            initialMedications = await loadProcedureMedications(patient, props.entityId);
            setMedications(initialMedications);
        }

        if (initialMedications && initialMedications.size > 0 && props.handleChangePatientUsesMedication) {
            await props.handleChangePatientUsesMedication(true);
        }
    }

    function setMedicationsRows() {
        if (!medications) {
            return;
        }

        let arePickableMedicationsAvailable = false;
        let currentHisMedicationsCount = 0;
        let currentManualMedicationsCount = 0;
        const currentHisTableRows = [];
        const currentManualTableRows = [];

        let totalManualMedications = 0;
        for (const [key, medication] of medications.entrySeq().toArray() as [string, MedicationRecord][]) {
            if (medication.action === ProfessionalSummaryDataAction.add) {
                currentManualTableRows.push(
                    <MedicationsAddEditTableRow
                        errors={props.errors}
                        index={currentHisMedicationsCount + totalManualMedications}
                        key={key}
                        medication={medication}
                        medicationKey={key}
                        medications={medications}
                        onChange={async (newMedications) => await onChange(newMedications, true)}
                        validateMedications={props.validateMedications}
                    />
                );
                totalManualMedications++;
                if (medication.name) {
                    currentManualMedicationsCount++;
                }
            } else {
                arePickableMedicationsAvailable = true;
                currentHisTableRows.push(
                    <MedicationsPickableTableRow
                        key={key}
                        medication={medication}
                        medicationKey={key}
                        medications={medications}
                        onChange={async (newMedications) => await onChange(newMedications, true)}
                    />
                );
                if (medication.action === ProfessionalSummaryDataAction.pick) {
                    currentHisMedicationsCount++;
                }
            }
        }

        setHasPickableMedications(arePickableMedicationsAvailable);
        setManualMedicationsCount(currentManualMedicationsCount);
        setHisMedicationsCount(currentHisMedicationsCount);
        setHisTableRows(currentHisTableRows);
        setManualTableRows(currentManualTableRows);
    }

    async function onChange(changedMedications: Medications, skipAddMedications?: boolean) {
        setMedications(changedMedications);
        const med = filterAndPrepareMedicationsForSaving(changedMedications, skipAddMedications);
        await props.onChange(med);
    }

    async function addEmptyMedicationRecord() {
        await onChange(medications!.set(uuidv4(), new MedicationRecord()));
    }

    async function selectActions(action: MedicationsSelectActions) {
        let finalMedication;
        switch (action) {
            case MedicationsSelectActions.all:
                finalMedication = medications!.map((medication) => {
                    if (medication && medication.action === ProfessionalSummaryDataAction.skip) {
                        return medication.set('action', ProfessionalSummaryDataAction.pick);
                    } else {
                        return medication;
                    }
                });
                break;
            case MedicationsSelectActions.current:
                finalMedication = medications!.map((medication) => {
                    if (medication && medication.action !== ProfessionalSummaryDataAction.add) {
                        return medication.set(
                            'action',
                            isCurrentMedication(medication)
                                ? ProfessionalSummaryDataAction.pick
                                : ProfessionalSummaryDataAction.skip
                        );
                    } else {
                        return medication;
                    }
                });
                break;
            case MedicationsSelectActions.none:
                finalMedication = medications!.map((medication) => {
                    if (medication && medication.action === ProfessionalSummaryDataAction.pick) {
                        return medication.set('action', ProfessionalSummaryDataAction.skip);
                    } else {
                        return medication;
                    }
                });
                break;
            default:
                never(action);
        }

        await onChange(finalMedication as Medications);
    }

    async function handlePatientUsesMedicationChange(value: string) {
        if (props.handleChangePatientUsesMedication) {
            await props.handleChangePatientUsesMedication(value === YesOrNo.yes ? true : false);
            if (medications && value === YesOrNo.no) {
                const newMedications = medications!.map((medication) => {
                    if (medication?.action === ProfessionalSummaryDataAction.pick) {
                        return medication.set('action', ProfessionalSummaryDataAction.skip);
                    } else {
                        return medication;
                    }
                });
                const finalMedications = (newMedications as Medications)
                    .filter((medication) => medication?.action !== ProfessionalSummaryDataAction.add)
                    .toOrderedMap();
                await onChange(finalMedications);
                setIsManualMedicationsOpen(false);
                setIsHisMedicationsOpen(false);
            }
        }
    }
}
